/**
 * FeatureManager - Manages feature plugins and routes messages (Frontend)
 *
 * Feature Lifecycle:
 * 1. registerFeature() - Registers feature, calls lightweight initialize()
 * 2. activateFeature() - Called when view mounts, initializes DOM
 * 3. deactivateFeature() - Called when view unmounts, cleans up DOM
 */
export class FeatureManager {
    constructor(coreAPI) {
        this.core = coreAPI;
        this.features = new Map();  // featureId -> feature instance
        this.messageHandlers = new Map();  // featureId -> handler function
        this.metadata = new Map();  // featureId -> metadata
        this.remoteCapabilities = {};  // Capabilities from remote peer (desktop)
        this.failedFeatures = new Map();  // featureId -> error (for debugging)
    }

    /**
     * Register a feature plugin (lightweight, no DOM)
     * @param {Class} FeatureClass - Feature class with static getMetadata() and instance methods
     * @returns {boolean} true if successful, false if failed
     */
    async registerFeature(FeatureClass) {
        let metadata;
        try {
            metadata = FeatureClass.getMetadata();
        } catch (error) {
            console.error(`[FeatureManager] Failed to get metadata from feature class:`, error);
            return false;
        }

        console.log(`[FeatureManager] Registering feature: ${metadata.id} v${metadata.version}`);

        // Check dependencies
        if (metadata.dependencies) {
            for (const dep of metadata.dependencies) {
                if (!this.features.has(dep)) {
                    console.warn(`[FeatureManager] Feature ${metadata.id} requires ${dep} which is not registered - skipping`);
                    this.failedFeatures.set(metadata.id, new Error(`Missing dependency: ${dep}`));
                    return false;
                }
            }
        }

        // Instantiate feature
        let feature;
        try {
            feature = new FeatureClass(this.core);
        } catch (error) {
            console.error(`[FeatureManager] Failed to instantiate ${metadata.id}:`, error);
            this.failedFeatures.set(metadata.id, error);
            return false;
        }

        // Initialize feature (should be lightweight, no DOM)
        try {
            await feature.initialize();
        } catch (error) {
            console.error(`[FeatureManager] Failed to initialize ${metadata.id}:`, error);
            console.error(`[FeatureManager] Stack trace:`, error.stack);
            this.failedFeatures.set(metadata.id, error);
            return false;
        }

        // Store feature
        this.features.set(metadata.id, feature);
        this.metadata.set(metadata.id, metadata);
        this.messageHandlers.set(metadata.id, feature.handleMessage.bind(feature));

        console.log(`[FeatureManager] ✓ Registered feature: ${metadata.id} v${metadata.version}`);
        return true;
    }

    /**
     * Register multiple features, continuing even if some fail
     * @param {Array<Class>} featureClasses - Array of feature classes
     * @returns {Object} { succeeded: string[], failed: string[] }
     */
    async registerFeatures(featureClasses) {
        const succeeded = [];
        const failed = [];

        for (const FeatureClass of featureClasses) {
            const metadata = FeatureClass.getMetadata();
            const success = await this.registerFeature(FeatureClass);
            if (success) {
                succeeded.push(metadata.id);
            } else {
                failed.push(metadata.id);
            }
        }

        if (failed.length > 0) {
            console.warn(`[FeatureManager] Failed to register features: ${failed.join(', ')}`);
        }

        return { succeeded, failed };
    }

    /**
     * Activate a feature (called when view mounts)
     * @param {string} featureId - Feature ID to activate
     * @returns {boolean} true if successful
     */
    async activateFeature(featureId) {
        const feature = this.features.get(featureId);
        if (!feature) {
            console.warn(`[FeatureManager] Cannot activate unknown feature: ${featureId}`);
            return false;
        }

        try {
            console.log(`[FeatureManager] Activating feature: ${featureId}`);
            await feature.activate();
            console.log(`[FeatureManager] ✓ Activated feature: ${featureId}`);
            return true;
        } catch (error) {
            console.error(`[FeatureManager] Failed to activate ${featureId}:`, error);
            return false;
        }
    }

    /**
     * Deactivate a feature (called when view unmounts)
     * @param {string} featureId - Feature ID to deactivate
     * @returns {boolean} true if successful
     */
    async deactivateFeature(featureId) {
        const feature = this.features.get(featureId);
        if (!feature) {
            console.warn(`[FeatureManager] Cannot deactivate unknown feature: ${featureId}`);
            return false;
        }

        try {
            console.log(`[FeatureManager] Deactivating feature: ${featureId}`);
            await feature.deactivate();
            console.log(`[FeatureManager] ✓ Deactivated feature: ${featureId}`);
            return true;
        } catch (error) {
            console.error(`[FeatureManager] Failed to deactivate ${featureId}:`, error);
            return false;
        }
    }

    /**
     * Activate multiple features
     * @param {Array<string>} featureIds - Feature IDs to activate
     */
    async activateFeatures(featureIds) {
        await Promise.all(featureIds.map(id => this.activateFeature(id)));
    }

    /**
     * Deactivate multiple features
     * @param {Array<string>} featureIds - Feature IDs to deactivate
     */
    async deactivateFeatures(featureIds) {
        for (const featureId of featureIds) {
            await this.deactivateFeature(featureId);
        }
    }

    /**
     * Route an incoming message to the appropriate feature
     * @param {Object} message - Feature message
     * @returns {Promise<any>} Response from feature
     */
    async routeMessage(message) {
        const { feature, cmd, version, payload, messageId } = message;

        const handler = this.messageHandlers.get(feature);
        if (!handler) {
            console.warn(`[FeatureManager] Unknown feature: ${feature}`);
            return;
        }

        try {
            // Pass message with 'action' field for backward compatibility with feature handlers
            const result = await handler({ ...message, action: cmd });
            return result;
        } catch (error) {
            console.error(`[FeatureManager] Error handling ${feature}.${cmd}:`, error);
            throw error;
        }
    }

    /**
     * Set remote capabilities (from desktop)
     * @param {Object} capabilities - Capabilities object from remote
     */
    setRemoteCapabilities(capabilities) {
        this.remoteCapabilities = capabilities;
        console.log('[FeatureManager] Remote capabilities:', capabilities);

        // Notify features of remote capabilities
        for (const feature of this.features.values()) {
            if (feature.onRemoteCapabilities) {
                feature.onRemoteCapabilities(capabilities);
            }
        }
    }

    /**
     * Check if remote supports a feature
     * @param {string} featureId - Feature ID
     * @returns {boolean}
     */
    isRemoteFeatureAvailable(featureId) {
        return !!this.remoteCapabilities[featureId];
    }

    /**
     * Get all feature capabilities for negotiation
     * @returns {Object} Capabilities object
     */
    getCapabilities() {
        const capabilities = {};
        for (const [id, metadata] of this.metadata) {
            capabilities[id] = {
                version: metadata.version,
                capabilities: metadata.capabilities || [],
                name: metadata.name
            };
        }
        return capabilities;
    }

    /**
     * Get a specific feature instance
     * @param {string} featureId - Feature ID
     * @returns {Object|null} Feature instance or null
     */
    getFeature(featureId) {
        return this.features.get(featureId) || null;
    }

    /**
     * Check if a feature is registered
     * @param {string} featureId - Feature ID
     * @returns {boolean}
     */
    hasFeature(featureId) {
        return this.features.has(featureId);
    }

    /**
     * Cleanup all features
     */
    async cleanup() {
        console.log('[FeatureManager] Cleaning up all features...');
        for (const [id, feature] of this.features) {
            try {
                if (feature.cleanup) {
                    await feature.cleanup();
                }
            } catch (error) {
                console.error(`[FeatureManager] Error cleaning up ${id}:`, error);
            }
        }
        this.features.clear();
        this.messageHandlers.clear();
        this.metadata.clear();
    }
}
